/**
 * The module catalog: one description of every built-in module, page,
 * feature and template (brief §4.1). The data is `catalog.json`; this file
 * types it, checks it, and resolves it into the module TREE that every
 * registry in the app is derived from (`registries.ts`).
 *
 * The API embeds the same `catalog.json` (api/Modules/Catalog/ModuleCatalog.cs)
 * and derives the page registry and the tools registry from it, so the web
 * and the API can never disagree about which pages exist. Both sides refuse a
 * field they do not model (this file's `catalogProblems`, the API's strict
 * deserialiser), so a field added on one side fails the other side's tests.
 *
 * The shape, field by field, and the reasons behind its few odd corners
 * (`rightsOrder`, `searchOrder`, `legacyNavHidden`, `offRail`): docs/module-catalog.md.
 */
import raw from './catalog.json';
import { ALWAYS_ON_MODULES, EMPTY_MODULE_CONFIG, teamModules, type TenantModuleConfig } from './config';
import { placeTemplates } from './templates';

// ── The file ────────────────────────────────────────────────────────────

export interface Catalog {
  version: 1;
  /** Hub order: the Hub tiles, the sidebar switcher, the accents. */
  modules: CatalogModule[];
  rights: CatalogRightsLayout;
  /**
   * The order `MODULE_NAV` is keyed in, every shell module once. The ⌘K page
   * index walks it and its order breaks ties between rows that share a label
   * ("Overview", "Meetings"); `HOT_PLATE_MODULES` follows it too. It is the
   * historical key order, kept so the refactor changed nothing.
   */
  searchOrder: string[];
  templates: CatalogTemplate[];
}

export interface CatalogRightsLayout {
  /** The module whose page grants are stored as BARE slugs (the legacy CS/OPS bucket). */
  bare: string;
  /** Modules granted as `{tile}:{slug}`, in the rights matrix's order. */
  modules: string[];
}

export type ModuleSurface = 'shell' | 'standalone' | 'guest';
export type ModuleAccess = 'standard' | 'owner-only' | 'role-only' | 'always';
export type OnOff = 'on' | 'off';

export interface CatalogModule {
  /** Route slug, stable forever ('coportal'). */
  id: string;
  /** Rights slug, stable forever ('cs-portal'): `allowed_tools`, `hub:tool`, `RequireTile`. */
  tile: string;
  /** The Asian Trails default label: the Hub tile, the rights matrix, the tools registry. */
  label: string;
  /** A shorter label for the sidebar and the breadcrumb, when it differs ('Bizdev'). */
  shortLabel?: string;
  /** The module's printed ink, a token NAME ('--c-cs'), never a colour. */
  ink: string;
  /** A key of `MODULE_ICONS` in shared/icons. */
  icon: string;
  route: string;
  surface: ModuleSurface;
  default: OnOff;
  /** 'key-agent', 'integration:beeper', 'template-source', 'core', … */
  tags?: string[];
  /**
   * Today's tile rule as data (`tileVisibleFor`): 'standard' (tool grant, else
   * the role list), 'owner-only' (an explicit grant only, admins included),
   * 'role-only' (the role list only), 'always' (everyone signed in).
   */
  access: ModuleAccess;
  /** Roles that see the tile without a tool grant (`allowedRoles`). */
  roles: string[];
  /** Present when the tile is a grantable hub TOOL (`allowed_tools`, the tools registry). */
  tool?: { defaultRoles: string[] };
  /** Listed in the sidebar's module switcher. */
  switcher?: boolean;
  /** One line under the module in the switcher. */
  blurb?: string;
  /**
   * HR: the tile also opens for anyone with appraisal access, and they land
   * on `nonAdminRoute` instead of the module's front page.
   */
  appraisalsAware?: boolean;
  nonAdminRoute?: string;
  /** One line on the Hub tile. */
  hint: string;
  /** Owns a `<route>/hot-plate` team board (the top bar's Hot Plate button). */
  hotPlate?: boolean;
  /** API route prefixes the module owns ('api/lemax'). The longest matching prefix wins. */
  api?: string[];
  /** Background jobs the module owns. */
  jobs?: string[];
  features?: CatalogFeature[];
  /** The sidebar's sections, in order, and every page of the module. */
  groups: CatalogGroup[];
  /**
   * The rights registry's page order, when it is not the order of the pages
   * below (CS/OPS, Bizdev). Every page with `rights`, once.
   */
  rightsOrder?: string[];
}

export interface CatalogGroup {
  /** Stable id within the module ('operations'), for tenant overrides. */
  id: string;
  /** Section label. Omitted for a module whose rail is one flat list. */
  heading?: string;
  /** The section's ink, a `--c-*` token NAME; omitted = the module's ink. */
  ink?: string;
  pages: CatalogPage[];
}

export interface CatalogPage {
  /** Route segment and rights token ('hotline-schedule'), stable forever. */
  slug: string;
  label: string;
  subtitle?: string;
  /** The route, when it is not `<module.route>/<slug>`. */
  path?: string;
  /** Not a sidebar row (a rights-only page, or a page reached another way). */
  navHidden?: boolean;
  /** A real, named route that is not a rail row: the breadcrumb names it. */
  offRail?: boolean;
  /** The breadcrumb's label for an `offRail` page, when it differs from `label`. */
  crumb?: string;
  /**
   * Show the sidebar row only to holders of this tile / appraisal access;
   * `setup`: only once the company completed that setup (`whatsapp`: a
   * number connected). The API decides it (`GET /api/modules` → `unavailable`).
   */
  requires?: { tile?: string; appraisals?: boolean; setup?: string };
  /** In the rights registry: a grantable page. Absent = not grantable. */
  rights?: CatalogPageRights;
  /** API route prefixes the page owns. */
  api?: string[];
  features?: CatalogFeature[];
  /**
   * A row this module shows for ANOTHER module's page ('coportal.destinations'):
   * a nav regroup, never a move. The row opens the lender's route and keeps
   * the lender's rights slug; while the borrowing module is on, the lender's
   * own rail row is hidden (`handOverBorrowedRows`). Guest Portal ▸ Content.
   */
  borrows?: string;
}

/** The rights registry's fields (`PageRegistryEntry`), defaults omitted. */
export interface CatalogPageRights {
  /** The rights matrix group, when it is not the containing section's heading. */
  group?: string;
  /** A lucide icon name, as the PHP registry spelled it. */
  icon?: string;
  adminOnly?: boolean;
  /** PHP's `nav_hidden` (the wire name): the rights matrix skips the page. */
  legacyNavHidden?: boolean;
  freeAccess?: boolean;
  skipRights?: boolean;
  parent?: string;
  moved?: string;
  editable?: boolean;
  explicitOnly?: boolean;
}

export interface CatalogFeature {
  /** 'coportal.logs.key-agent-filter', stable forever. */
  id: string;
  label: string;
  default: OnOff;
  jobs?: string[];
}

export interface CatalogTemplate {
  id: string;
  title: string;
  kind: 'code' | 'spec' | 'config' | 'integration';
  blurb: string;
  /** The built-in module whose code backs the template. */
  module?: string;
  /** The built-in page whose code backs the template ('coportal.hotline-schedule'). */
  page?: string;
  /** Where it may be installed: a module id, or null for the top level. */
  allowParent: (string | null)[];
  /** A JSON Schema for the tenant's settings. */
  config: Record<string, unknown>;
}

export const CATALOG: Catalog = raw as unknown as Catalog;

// ── The tree ────────────────────────────────────────────────────────────

/**
 * The effective module tree: the catalog with every id and route resolved,
 * and each module, page and feature switched on or off.
 *
 * With no tenant config it is the catalog's defaults (every built-in module
 * is on: Asian Trails as it has always been). The tenant module config
 * (brief §4.2, `config.ts`) merges here and only here: every registry is
 * derived from a tree (`registries.ts`), so the derivations do not change.
 */
export interface ModuleTree {
  modules: TreeModule[];
  rights: CatalogRightsLayout;
  searchOrder: string[];
}

export interface TreeModule extends Omit<CatalogModule, 'groups' | 'features'> {
  enabled: boolean;
  /**
   * Every row of this module lives inside another module's rail (a template
   * placed there, brief §5.3): it has no Hub tile and no switcher entry, and
   * its routes answer to the host's rail. Its routes and rights are its own.
   */
  hostedIn?: string;
  /** A team module (Admin ▸ Modules ▸ Blank team module), not in the catalog. */
  team?: boolean;
  /** The sidebar / short label: the tenant's label, else `shortLabel ?? label`. */
  navLabel: string;
  features: TreeFeature[];
  groups: TreeGroup[];
}

export interface TreeGroup extends Omit<CatalogGroup, 'pages'> {
  pages: TreePage[];
}

export interface TreePage extends Omit<CatalogPage, 'features'> {
  /** '<module>.<slug>' ('coportal.hotline-schedule'). */
  id: string;
  module: string;
  /** The resolved route. */
  to: string;
  enabled: boolean;
  features: TreeFeature[];
}

export interface TreeFeature extends CatalogFeature {
  enabled: boolean;
}

/**
 * The catalog merged with a tenant's module config. The rules (the API's
 * `EffectiveModules` applies the same ones):
 *
 *   - a module is on unless the tenant switched it off (or its catalog
 *     default is off and the tenant did not switch it on); Admin and
 *     Preferences are always on;
 *   - a page is on when its module is on, the tenant did not switch it off,
 *     and its rights parent in the same module (`rights.parent`) is on: the
 *     Hotline Schedule's edit right goes with the Hotline Schedule;
 *   - a feature is on when its owner (page or module) is on and the tenant
 *     did not switch it off;
 *   - labels, group headings, the icon, the ink and the access rule are
 *     replaced where the tenant set one; `order` reorders the Hub (unlisted
 *     modules keep their catalog order, after the listed ones);
 *   - team modules (`installed`, template `team-module`) join as modules of
 *     their own at `/m/<id>`: a Board and My notes, for everyone on staff;
 *   - a code template placed inside another module moves its rail rows
 *     there (`placeTemplates`, templates.ts).
 */
export function resolveModuleTree(catalog: Catalog = CATALOG, config?: TenantModuleConfig | null): ModuleTree {
  const cfg = config ?? EMPTY_MODULE_CONFIG;
  const feature = (f: CatalogFeature, ownerOn: boolean): TreeFeature => ({
    ...f,
    enabled: ownerOn && (cfg.features[f.id]?.enabled ?? f.default === 'on'),
  });
  const modules = catalog.modules.map((m): TreeModule => {
    const mo = cfg.modules[m.id] ?? {};
    const enabled = ALWAYS_ON_MODULES.includes(m.id) || (mo.enabled ?? m.default === 'on');
    const switchedOff = new Set<string>();
    for (const g of m.groups) {
      for (const p of g.pages) if (cfg.pages[`${m.id}.${p.slug}`]?.enabled === false) switchedOff.add(p.slug);
    }
    const parentOf = new Map<string, string>();
    for (const g of m.groups) for (const p of g.pages) if (p.rights?.parent) parentOf.set(p.slug, p.rights.parent);
    const pageOn = (slug: string, depth = 0): boolean =>
      !switchedOff.has(slug) && (depth > 8 || !parentOf.has(slug) || pageOn(parentOf.get(slug)!, depth + 1));
    const out: TreeModule = {
      ...m,
      enabled,
      navLabel: mo.label ?? m.shortLabel ?? m.label,
      features: (m.features ?? []).map((f) => feature(f, enabled)),
      groups: m.groups.map((g) => {
        const heading = mo.groups?.[g.id]?.heading;
        return {
          ...g,
          ...(heading && g.heading !== undefined ? { heading } : {}),
          pages: g.pages.map((p) => {
            const id = `${m.id}.${p.slug}`;
            const on = enabled && pageOn(p.slug);
            const po = cfg.pages[id];
            return {
              ...p,
              ...(po?.label ? { label: po.label } : {}),
              id,
              module: m.id,
              to: p.path ?? `${m.route}/${p.slug}`,
              enabled: on,
              features: (p.features ?? []).map((f) => feature(f, on)),
            };
          }),
        };
      }),
    };
    if (mo.label) out.label = mo.label;
    if (mo.icon) out.icon = mo.icon;
    if (mo.ink) out.ink = mo.ink;
    if (mo.access) out.access = mo.access;
    return out;
  });
  const teams = teamModules(cfg).filter((t) => !catalog.modules.some((m) => m.id === t.id));
  for (const t of teams) modules.push(teamTreeModule(t));
  if (cfg.installed.length) placeTemplates(catalog, cfg, modules);
  handOverBorrowedRows(modules);
  if (cfg.order.length) {
    const rank = (id: string) => {
      const i = cfg.order.indexOf(id);
      return i < 0 ? Number.MAX_SAFE_INTEGER : i;
    };
    modules.sort((a, b) => rank(a.id) - rank(b.id));
  }
  const searchOrder = teams.length ? [...catalog.searchOrder, ...teams.map((t) => t.id)] : catalog.searchOrder;
  return { rights: catalog.rights, searchOrder, modules };
}

/** Who sees a team module: everyone on staff (never a viewer or an agent). */
export const TEAM_MODULE_ROLES: readonly string[] = ['admin', 'manager', 'user'];

/** A team module as a tree module: a Board and My notes at `/m/<id>`. */
export function teamTreeModule(t: { id: string; label: string; icon: string; ink: string }): TreeModule {
  const route = `/m/${t.id}`;
  const page = (slug: string, label: string): TreePage => ({
    slug,
    label,
    id: `${t.id}.${slug}`,
    module: t.id,
    to: `${route}/${slug}`,
    enabled: true,
    features: [],
  });
  return {
    id: t.id,
    tile: `team-${t.id}`,
    label: t.label,
    navLabel: t.label,
    ink: t.ink,
    icon: t.icon,
    route,
    surface: 'shell',
    default: 'on',
    tags: ['team'],
    access: 'role-only',
    roles: [...TEAM_MODULE_ROLES],
    switcher: true,
    blurb: 'The team board and notes',
    hint: 'What the team is working on, and your notes',
    enabled: true,
    team: true,
    features: [],
    groups: [{ id: 'main', pages: [page('board', 'Board'), page('my-notes', 'My notes')] }],
  };
}

/**
 * Borrowed rows (`CatalogPage.borrows`), resolved in place: the rule "a page
 * shown by another module that is on leaves its own module's rail".
 *
 *   - the borrowing row opens the lender's route (`to`), and is on only while
 *     both its own module and the lender page are on;
 *   - while the borrowing row is on, the lender's row becomes `navHidden`:
 *     it leaves its own rail but stays a real, grantable, reachable page (its
 *     route, rights slug and gates are untouched, so a hidden row is never a
 *     switched-off page).
 *
 * Guest Portal ▸ Content borrows CS/OPS's Guest Catalogue, General Info and
 * Gift list: with the Guest Portal off (the Asian Trails default) nothing
 * changes; on, those rows move from the CS/OPS rail to the Guest Portal's.
 * The API mirrors it in `EffectiveModules.Resolve`.
 */
export function handOverBorrowedRows(modules: TreeModule[]): void {
  const pages = new Map<string, TreePage>();
  for (const m of modules) for (const g of m.groups) for (const p of g.pages) pages.set(p.id, p);
  for (const m of modules) {
    for (const g of m.groups) {
      g.pages = g.pages.map((p) => {
        if (!p.borrows) return p;
        const lender = pages.get(p.borrows);
        if (!lender) return { ...p, enabled: false };
        return { ...p, to: lender.to, enabled: p.enabled && lender.enabled };
      });
    }
  }
  const handedOver = new Set<string>();
  for (const m of modules) for (const g of m.groups) for (const p of g.pages) if (p.borrows && p.enabled) handedOver.add(p.borrows);
  if (!handedOver.size) return;
  for (const m of modules) {
    for (const g of m.groups) {
      g.pages = g.pages.map((p) => (handedOver.has(p.id) ? { ...p, navHidden: true } : p));
    }
  }
}

/** The Asian Trails tree: the catalog's defaults, no tenant config. */
export const DEFAULT_MODULE_TREE: ModuleTree = resolveModuleTree(CATALOG);

/** The pages of a module in rights-registry order (`rightsOrder`, else page order). */
export function rightsPages(m: TreeModule): TreePage[] {
  const pages = m.groups.flatMap((g) => g.pages).filter((p) => p.rights);
  if (!m.rightsOrder) return pages;
  const bySlug = new Map(pages.map((p) => [p.slug, p]));
  return m.rightsOrder.map((s) => bySlug.get(s)).filter((p): p is TreePage => !!p);
}

// ── The check ───────────────────────────────────────────────────────────

const KEYS = {
  catalog: { req: ['version', 'modules', 'rights', 'searchOrder', 'templates'], opt: [] },
  rights: { req: ['bare', 'modules'], opt: [] },
  module: {
    req: ['id', 'tile', 'label', 'ink', 'icon', 'route', 'surface', 'default', 'access', 'roles', 'hint', 'groups'],
    opt: [
      'shortLabel', 'tags', 'tool', 'switcher', 'blurb', 'appraisalsAware', 'nonAdminRoute',
      'hotPlate', 'api', 'jobs', 'features', 'rightsOrder',
    ],
  },
  tool: { req: ['defaultRoles'], opt: [] },
  group: { req: ['id', 'pages'], opt: ['heading', 'ink'] },
  page: {
    req: ['slug', 'label'],
    opt: ['subtitle', 'path', 'navHidden', 'offRail', 'crumb', 'requires', 'rights', 'api', 'features', 'borrows'],
  },
  requires: { req: [], opt: ['tile', 'appraisals', 'setup'] },
  pageRights: {
    req: [],
    opt: [
      'group', 'icon', 'adminOnly', 'legacyNavHidden', 'freeAccess', 'skipRights', 'parent', 'moved',
      'editable', 'explicitOnly',
    ],
  },
  feature: { req: ['id', 'label', 'default'], opt: ['jobs'] },
  template: { req: ['id', 'title', 'kind', 'blurb', 'allowParent', 'config'], opt: ['module', 'page'] },
} as const;

const BOOLEAN_FLAGS = new Set([
  'switcher', 'appraisalsAware', 'hotPlate', 'navHidden', 'offRail', 'appraisals', 'adminOnly',
  'legacyNavHidden', 'freeAccess', 'skipRights', 'editable', 'explicitOnly',
]);

/**
 * Everything wrong with a catalog, as readable lines (empty = sound). The
 * shape (no unknown or missing field, flags only ever `true`, so the API's
 * round-trip reads the file back unchanged), the ids (unique, slug-shaped,
 * pages unique per module) and the cross-references (rights layout,
 * rightsOrder, searchOrder, template modules and pages).
 */
export function catalogProblems(c: unknown): string[] {
  const out: string[] = [];
  const shape = (where: string, o: unknown, kind: keyof typeof KEYS): o is Record<string, unknown> => {
    if (!o || typeof o !== 'object' || Array.isArray(o)) {
      out.push(`${where}: not an object`);
      return false;
    }
    const { req, opt } = KEYS[kind];
    const known = new Set<string>([...req, ...opt]);
    for (const k of req) if (!(k in o)) out.push(`${where}: missing "${k}"`);
    for (const [k, v] of Object.entries(o)) {
      if (!known.has(k)) out.push(`${where}: unknown field "${k}"`);
      if (BOOLEAN_FLAGS.has(k) && v !== true) out.push(`${where}: "${k}" is written only as true`);
      if (v === null && kind !== 'template') out.push(`${where}: "${k}" is null (omit it)`);
      if (Array.isArray(v) && v.length === 0 && !req.includes(k as never)) {
        out.push(`${where}: "${k}" is empty (omit it)`);
      }
    }
    return true;
  };
  const SLUG = /^[a-z][a-z0-9-]*$/;
  const INK = /^--[a-z][a-z0-9-]*$/;

  if (!shape('catalog', c, 'catalog')) return out;
  const cat = c as unknown as Catalog;
  if (cat.version !== 1) out.push('catalog: version is not 1');
  shape('rights', cat.rights, 'rights');

  const moduleIds = new Set<string>();
  const tiles = new Set<string>();
  const pageIds = new Set<string>();
  const featureIds = new Set<string>();
  const checkFeatures = (where: string, fs: CatalogFeature[] | undefined) => {
    for (const f of fs ?? []) {
      if (!shape(`${where} feature ${f.id}`, f, 'feature')) continue;
      if (featureIds.has(f.id)) out.push(`feature ${f.id}: duplicate id`);
      featureIds.add(f.id);
      if (f.default !== 'on' && f.default !== 'off') out.push(`feature ${f.id}: default is not on/off`);
    }
  };

  for (const m of cat.modules ?? []) {
    const where = `module ${m?.id}`;
    if (!shape(where, m, 'module')) continue;
    if (!SLUG.test(m.id)) out.push(`${where}: id is not a slug`);
    if (moduleIds.has(m.id)) out.push(`${where}: duplicate id`);
    moduleIds.add(m.id);
    if (tiles.has(m.tile)) out.push(`${where}: duplicate tile ${m.tile}`);
    tiles.add(m.tile);
    if (!INK.test(m.ink)) out.push(`${where}: ink is not a token name`);
    if (!['shell', 'standalone', 'guest'].includes(m.surface)) out.push(`${where}: bad surface`);
    if (!['on', 'off'].includes(m.default)) out.push(`${where}: default is not on/off`);
    if (!['standard', 'owner-only', 'role-only', 'always'].includes(m.access)) out.push(`${where}: bad access`);
    if (m.tool) shape(`${where} tool`, m.tool, 'tool');
    if (m.switcher && !m.blurb) out.push(`${where}: in the switcher without a blurb`);
    if (m.appraisalsAware && !m.nonAdminRoute) out.push(`${where}: appraisalsAware without nonAdminRoute`);
    checkFeatures(where, m.features);

    const slugs = new Set<string>();
    const rightsSlugs: string[] = [];
    for (const g of m.groups ?? []) {
      const gw = `${where} group ${g?.id}`;
      if (!shape(gw, g, 'group')) continue;
      if (g.ink && !INK.test(g.ink)) out.push(`${gw}: ink is not a token name`);
      for (const p of g.pages ?? []) {
        const pw = `${where} page ${p?.slug}`;
        if (!shape(pw, p, 'page')) continue;
        if (!SLUG.test(p.slug)) out.push(`${pw}: slug is not a slug`);
        if (slugs.has(p.slug)) out.push(`${pw}: duplicate slug in the module`);
        slugs.add(p.slug);
        pageIds.add(`${m.id}.${p.slug}`);
        if (p.offRail && !p.navHidden) out.push(`${pw}: offRail but a rail row`);
        if (p.crumb && !p.offRail) out.push(`${pw}: crumb without offRail`);
        if (p.requires) shape(`${pw} requires`, p.requires, 'requires');
        if (p.rights) {
          shape(`${pw} rights`, p.rights, 'pageRights');
          rightsSlugs.push(p.slug);
          if (!g.heading && !p.rights.group) out.push(`${pw}: a rights page in a section with no heading needs rights.group`);
        }
        checkFeatures(pw, p.features);
      }
    }
    if (m.rightsOrder) {
      const a = [...m.rightsOrder].sort().join();
      const b = [...rightsSlugs].sort().join();
      if (a !== b) out.push(`${where}: rightsOrder is not exactly its rights pages`);
    }
  }

  const rights = cat.rights;
  if (rights) {
    if (!moduleIds.has(rights.bare)) out.push(`rights.bare: unknown module ${rights.bare}`);
    for (const id of rights.modules ?? []) if (!moduleIds.has(id)) out.push(`rights.modules: unknown module ${id}`);
    if (rights.modules?.includes(rights.bare)) out.push('rights: the bare module is also namespaced');
    for (const m of cat.modules ?? []) {
      const granted = m.id === rights.bare || rights.modules?.includes(m.id);
      const hasRights = m.groups?.some((g) => g.pages?.some((p) => p.rights));
      if (hasRights && !granted) out.push(`module ${m.id}: has rights pages but is not in the rights layout`);
    }
  }

  const shellIds = (cat.modules ?? []).filter((m) => m.surface === 'shell').map((m) => m.id);
  const so = cat.searchOrder ?? [];
  if ([...so].sort().join() !== [...shellIds].sort().join() || new Set(so).size !== so.length) {
    out.push('searchOrder: not exactly the shell modules, once each');
  }

  for (const m of cat.modules ?? []) {
    for (const g of m.groups ?? []) {
      for (const p of g.pages ?? []) {
        if (!p?.borrows) continue;
        const pw = `module ${m.id} page ${p.slug}`;
        if (!pageIds.has(p.borrows)) out.push(`${pw}: borrows unknown page ${p.borrows}`);
        else if (p.borrows.startsWith(`${m.id}.`)) out.push(`${pw}: borrows a page of its own module`);
        if (p.path || p.rights || p.api || p.navHidden) out.push(`${pw}: a borrowed row has no path, rights, api or navHidden of its own`);
        const [lm, ...rest] = p.borrows.split('.');
        const lender = cat.modules.find((x) => x.id === lm)?.groups.flatMap((x) => x.pages).find((x) => x.slug === rest.join('.'));
        if (lender?.borrows) out.push(`${pw}: borrows a borrowed row`);
      }
    }
  }

  const templateIds = new Set<string>();
  for (const t of cat.templates ?? []) {
    const tw = `template ${t?.id}`;
    if (!shape(tw, t, 'template')) continue;
    if (templateIds.has(t.id)) out.push(`${tw}: duplicate id`);
    templateIds.add(t.id);
    if (!['code', 'spec', 'config', 'integration'].includes(t.kind)) out.push(`${tw}: bad kind`);
    if (t.module && !moduleIds.has(t.module)) out.push(`${tw}: unknown module ${t.module}`);
    if (t.page && !pageIds.has(t.page)) out.push(`${tw}: unknown page ${t.page}`);
    for (const p of t.allowParent ?? []) if (p !== null && !moduleIds.has(p)) out.push(`${tw}: unknown parent ${p}`);
  }
  return out;
}
